import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Link, useLocation } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import StatusIcon from '../../components/StatusIcon';
import Button from '../../components/Button';
import DisplayDate from '../../components/DisplayDate';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as SwitchUserIcon } from '../../assets/svg/switch-user.svg';
import { ReactComponent as SwitchIcon } from '../../assets/svg/switch.svg';

// TYPES
import {
	ComplaintAdminDashboardComplaints,
	ComplaintUserDashboardComplaints,
	DomainProfileType
} from '../../api/models';

const Complaints: React.FC = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// State to manage Admin view
	const [canAccessAdminView, setCanAccessAdminView] = React.useState<boolean>(false);

	// Check if User is viewing Complaint as an Admin or not
	const viewComplaintAsAdmin = useAppSelector((state) => state.user.viewComplaintAsAdmin);

	// Check Profile Type of the Active User
	const activeProfileType = useAppSelector((state) => state.user.activeProfileType);

	// Check if User is a Staff  member
	const isStaffMember = useAppSelector((state) => state.user.activeProfileType === DomainProfileType.Staff);

	// State to manage maximum number of items per section
	const defaultMaxItems: number = 5;

	// Set up Admin Data state and initial Admin data
	const initialAdminData: ComplaintAdminDashboardComplaints = {
		newList: [],
		inProgressList: [],
		completedList: []
	};

	const [adminComplaintsData, setAdminComplaintsData] =
		React.useState<ComplaintAdminDashboardComplaints>(initialAdminData);

	const getAdminComplaintsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAdminComplaintsData();

		response.result = response.result || initialAdminData;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setAdminComplaintsData(response.result);
		}
	};

	// Set up User Data state and initial User data
	const initialUserData: ComplaintUserDashboardComplaints = {
		newList: [],
		inProgressList: [],
		completedList: []
	};

	const [userComplaintsData, setUserComplaintsData] = React.useState<ComplaintUserDashboardComplaints>(initialUserData);

	const getUserComplaintsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getUserComplaintsData();

		response.result = response.result || initialUserData;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setUserComplaintsData(response.result);
		}
	};

	// Check if user is a staff and change the view accordingly
	React.useEffect(() => {
		if (isStaffMember || isOrganisationAdmin) {
			setCanAccessAdminView(true);
		} else {
			setCanAccessAdminView(false);
		}
	}, []);

	// Check if User follow the user-view Link from Dashboard
	React.useEffect(() => {
		if (location.pathname === '/complaints/user-view') {
			dispatch(UserSlice.actions.update({ viewComplaintAsAdmin: false }));

			getUserComplaintsData();
		} else if (canAccessAdminView && location.pathname === '/complaints/admin-view') {
			dispatch(UserSlice.actions.update({ viewComplaintAsAdmin: true }));

			getAdminComplaintsData();
		}
	}, []);

	// Load data based on which view is loaded
	React.useEffect(() => {
		if (canAccessAdminView && viewComplaintAsAdmin) {
			getAdminComplaintsData();
		} else {
			getUserComplaintsData();
		}

		setIsLoading(false);
	}, [canAccessAdminView]);

	// FILTER ADMIN DATA
	const {
		newList: newAdminList,
		inProgressList: inProgressAdminList,
		completedList: completedAdminList
	} = adminComplaintsData;

	const [maxNewAdminList, setMaxNewAdminList] = React.useState<number>(defaultMaxItems);
	const [maxInProgressAdminList, setMaxInProgressAdminList] = React.useState<number>(defaultMaxItems);
	const [maxCompletedAdminList, setMaxCompletedAdminList] = React.useState<number>(defaultMaxItems);

	// FILTER USER DATA
	const {
		newList: newUserList,
		inProgressList: inProgressUserList,
		completedList: completedUserList
	} = userComplaintsData;

	const [maxNewUserList, setMaxNewUserList] = React.useState<number>(defaultMaxItems);
	const [maxInProgressUserList, setMaxInProgressUserList] = React.useState<number>(defaultMaxItems);
	const [maxCompletedUserList, setMaxCompletedUserList] = React.useState<number>(defaultMaxItems);

	// Switch Role Function
	const switchRole = () => {
		if (canAccessAdminView) {
			if (viewComplaintAsAdmin) {
				dispatch(UserSlice.actions.update({ viewComplaintAsAdmin: false }));

				getUserComplaintsData();
			} else {
				dispatch(UserSlice.actions.update({ viewComplaintAsAdmin: true }));

				getAdminComplaintsData();
			}
		}
	};

	return (
		<div className="page-general-layout complaints-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{canAccessAdminView && viewComplaintAsAdmin ? (
									<>
										<p className="sub-grey-text mbtom-30">
											Complaints are an important way for members of organisations to raise serious safety concerns. As
											a Leader, you can view, manage and resolve complaints received by your organisation. If you need
											assistance managing complaints, access our helpful guidance documents.
										</p>

										<div className="general-notification-box">
											<div className="box-left">
												<SwitchUserIcon />

												<div className="info-text">
													<p className="mbtom-8">
														Below are complaints received by your organisation. As a <strong>Leader</strong>, you can
														view, manage and resolve these complaints.
													</p>

													<p>
														You can also submit a complaint and view <strong>your own complaints</strong>. Click on
														‘View my complaints’ to switch roles.
													</p>
												</div>
											</div>

											<Button className="btn-primary small" onClick={() => switchRole()}>
												<SwitchIcon className="link-icon" /> View my complaints
											</Button>
										</div>

										{(newAdminList && newAdminList.length > 0) ||
										(inProgressAdminList && inProgressAdminList.length > 0) ||
										(completedAdminList && completedAdminList.length > 0) ? (
											<>
												{isOrganisationActive && (
													<div className="submit-btn-container">
														<Link to="/complaints-submit" className="btn-primary submit-btn">
															Submit a complaint
														</Link>
													</div>
												)}

												{newAdminList && newAdminList.length > 0 && (
													<section className="info-section complaints-page">
														<div className="section-header bold">
															New
															<span className="quantity">{newAdminList.length}</span>
														</div>

														{newAdminList.slice(0, maxNewAdminList).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row complaints">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="danger" />
																		</div>

																		<div className="col-8 col-lg-auto full-width">
																			<div className="row detail-info center">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Submitted</div>

																					<time className="name">
																						<DisplayDate>{item.created}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.createdBy}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.datesOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/complaints/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxNewAdminList < newAdminList.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxNewAdminList(newAdminList.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {newAdminList.length - maxNewAdminList} more
															</Button>
														)}
													</section>
												)}

												{inProgressAdminList && inProgressAdminList.length > 0 ? (
													<section className="info-section complaints-page">
														<div className="section-header bold">In Progress</div>

														{inProgressAdminList.slice(0, maxInProgressAdminList).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="warning" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info center">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Last updated</div>

																					<time className="name">
																						<DisplayDate>{item.updated}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.updatedBy}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.datesOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/complaints/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxInProgressAdminList < inProgressAdminList.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxInProgressAdminList(inProgressAdminList.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {inProgressAdminList.length - maxInProgressAdminList} more
															</Button>
														)}
													</section>
												) : null}

												{completedAdminList && completedAdminList.length > 0 ? (
													<section className="info-section complaints-page">
														<div className="section-header bold">Resolved</div>

														{completedAdminList.slice(0, maxCompletedAdminList).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="success" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info center">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Resolved</div>

																					<time className="name">
																						<DisplayDate>{item.resolved}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.resolvedBy}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.datesOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/complaints/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxCompletedAdminList < completedAdminList.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxCompletedAdminList(completedAdminList.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {completedAdminList.length - maxCompletedAdminList} more
															</Button>
														)}
													</section>
												) : null}
											</>
										) : (
											<div className="empty-notification">
												<h2 className="info-text">
													You have not yet submitted any <strong>Complaints</strong>.
												</h2>

												{isOrganisationActive ? (
													<Link to="/complaints-submit" className="btn-primary submit-btn">
														Submit a complaint
													</Link>
												) : null}
											</div>
										)}
									</>
								) : (
									<>
										<p className="sub-grey-text mbtom-30">
											Complaints are an important way for members of organisations to raise serious safety concerns. As
											a member of your organisation, you can submit a complaint and view your own complaints.{' '}
										</p>

										{canAccessAdminView && (
											<div className="general-notification-box">
												<div className="box-left">
													<SwitchUserIcon />

													<div className="info-text">
														{isOrganisationAdmin ? (
															<>
																<p>
																	Below are the complaints that <strong>you have submitted</strong> to your
																	organisation.
																</p>

																<p>
																	As a <strong>Leader</strong> , you can also view, manage and resolve complaints
																	received by your organisation. Click on ‘Manage complaints’ to switch roles.
																</p>
															</>
														) : (
															<p>
																Below are the complaints that <strong>you have submitted</strong> to your organisation.
															</p>
														)}
													</div>
												</div>

												<Button className="btn-primary small" onClick={() => switchRole()}>
													<SwitchIcon className="link-icon" /> Manage complaints
												</Button>
											</div>
										)}

										{(newUserList && newUserList.length > 0) ||
										(inProgressUserList && inProgressUserList.length > 0) ||
										(completedUserList && completedUserList.length > 0) ? (
											<>
												{isOrganisationActive && (
													<div className="submit-btn-container">
														<Link to="/complaints-submit" className="btn-primary submit-btn">
															Submit a complaint
														</Link>
													</div>
												)}

												{newUserList && newUserList.length > 0 && (
													<section className="info-section complaints-page">
														<div className="section-header bold">
															New
															<span className="quantity">{newUserList.length}</span>
														</div>

														{newUserList.slice(0, maxNewUserList).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row complaints">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="danger" />
																		</div>

																		<div className="col-8 col-lg-auto full-width">
																			<div className="row detail-info center">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Submitted</div>

																					<time className="name">
																						<DisplayDate>{item.created}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.createdBy}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.datesOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/complaints/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxNewUserList < newUserList.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxNewUserList(newUserList.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {newUserList.length - maxNewUserList} more
															</Button>
														)}
													</section>
												)}

												{inProgressUserList && inProgressUserList.length > 0 ? (
													<section className="info-section complaints-page">
														<div className="section-header bold">In Progress</div>

														{inProgressUserList.slice(0, maxInProgressUserList).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="warning" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info center">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Last updated</div>

																					<time className="name">
																						<DisplayDate>{item.updated}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.updatedBy}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.datesOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/complaints/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxInProgressUserList < inProgressUserList.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxInProgressUserList(inProgressUserList.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {inProgressUserList.length - maxInProgressUserList} more
															</Button>
														)}
													</section>
												) : null}

												{completedUserList && completedUserList.length > 0 ? (
													<section className="info-section complaints-page">
														<div className="section-header bold">Resolved</div>

														{completedUserList.slice(0, maxCompletedUserList).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="success" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info center">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Resolved</div>

																					<time className="name">
																						<DisplayDate>{item.resolved}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.resolvedBy}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.datesOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/complaints/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxCompletedUserList < completedUserList.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxCompletedUserList(completedUserList.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {completedUserList.length - maxCompletedUserList} more
															</Button>
														)}
													</section>
												) : null}
											</>
										) : (
											<div className="empty-notification">
												<h2 className="info-text">
													You have not yet submitted any <strong>Complaints</strong>.
												</h2>

												{isOrganisationActive && (
													<Link to="/complaints-submit" className="btn-primary submit-btn">
														Submit a complaint
													</Link>
												)}
											</div>
										)}
									</>
								)}

								{isOrganisationActive &&
									(activeProfileType === DomainProfileType.SuperAdministrator ||
										activeProfileType === DomainProfileType.Staff) && (
										<section className="general-guide">
											<h2 className="guide-header">Complaints management guides</h2>

											<p className="guide-info">
												If you need assistance managing complaints, review our helpful guidance documents.
											</p>

											<Link className="btn btn-primary guide-btn" to="/complaints-advice-templates">
												View Complaints Management Guides
											</Link>
										</section>
									)}

								<ScrollToTopButton offsetValue={100} />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default Complaints;
